import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Opportunity } from '../types';

interface CompanyInfo {
  name?: string;
  currency?: string;
  address?: string;
  email?: string;
  phone?: string;
  siret?: string;
}

interface OpportunityPDFModalProps {
  opportunity: Opportunity;
  companyInfo?: CompanyInfo;
  onClose: () => void;
}

const currencySymbol = (code?: string) => {
  switch (code) {
    case 'USD': return '$';
    case 'GBP': return '£';
    case 'CHF': return 'CHF';
    default: return '€';
  }
};

const OpportunityPDFModal: React.FC<OpportunityPDFModalProps> = ({ opportunity, companyInfo = {}, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const symbol = currencySymbol(companyInfo.currency);

  const handleDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem('orion_access_token') || '';
      const res = await fetch(`api/pdf_opportunity.php?id=${encodeURIComponent(String(opportunity.id))}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const contentType = res.headers.get('Content-Type') ?? '';
      if (!res.ok || !contentType.includes('application/pdf')) {
        const msg = await res.text().catch(() => '');
        throw new Error(msg || `Erreur HTTP ${res.status} (${contentType})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clientSlug = opportunity.clientCompanyName.replace(/[^a-zA-Z0-9]/g, '_');
      const refNum = `OPP-${String(opportunity.id).padStart(5, '0')}`;
      a.download = `Proposition_${clientSlug}_${refNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Impossible de générer le PDF.');
    } finally {
      setLoading(false);
    }
  };

  const refNum = `OPP-${String(opportunity.id).padStart(5, '0')}`;
  const dateStr = new Date(opportunity.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  const modal = (
    <div className="orion-modal-backdrop fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in">
      <div className="orion-modal-panel w-full max-w-3xl rounded-[2rem] shadow-2xl border animate-in zoom-in-95 flex flex-col max-h-[90vh]">

        {/* Toolbar */}
        <div className="flex items-center justify-between px-8 pt-7 pb-4 border-b border-slate-700/40 flex-shrink-0">
          <h3 className="text-base font-black dark:text-white">Aperçu — Proposition commerciale</h3>
          <div className="flex items-center gap-3">
            {error && (
              <span className="text-xs text-rose-400 font-bold max-w-[200px] truncate">{error}</span>
            )}
            <button
              type="button"
              onClick={handleDownload}
              disabled={loading}
              className="orion-primary-btn text-white px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 shadow-lg disabled:opacity-60"
            >
              {loading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
              )}
              {loading ? 'Génération...' : 'Télécharger PDF'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-300 p-2 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Document preview */}
        <div className="overflow-y-auto flex-1 custom-scrollbar">
          <div className="p-8">
            {/* White paper card */}
            <div className="bg-white rounded-2xl shadow-inner border border-slate-200 p-12 text-slate-900">

              {/* Header */}
              <div className="flex justify-between items-start mb-10 pb-6 border-b-2 border-slate-200">
                <div>
                  <div className="text-2xl font-black text-slate-900 tracking-tight">{companyInfo.name || ''}</div>
                  <div className="text-xs text-slate-500 mt-1 leading-relaxed">
                    {companyInfo.address && <span className="block">{companyInfo.address}</span>}
                    {companyInfo.email && <span className="block">{companyInfo.email}</span>}
                    {companyInfo.phone && <span className="block">{companyInfo.phone}</span>}
                    {companyInfo.siret && <span className="block">SIRET : {companyInfo.siret}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-black text-indigo-600 uppercase tracking-widest">Proposition</div>
                  <div className="text-xs text-slate-400 mt-1.5">Réf. {refNum}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{dateStr}</div>
                </div>
              </div>

              {/* Client */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-8">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Client / Prospect</div>
                <div className="text-lg font-black text-slate-900">{opportunity.clientCompanyName}</div>
              </div>

              {/* Subject */}
              <div className="mb-7">
                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Objet de la proposition</div>
                <div className="text-base font-bold text-slate-700">{opportunity.subject}</div>
              </div>

              {/* Items table */}
              <table className="w-full border-collapse mb-6">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="py-3 px-4 text-left text-[9px] font-black text-slate-500 uppercase tracking-widest rounded-l-lg">Description</th>
                    <th className="py-3 px-4 text-right text-[9px] font-black text-slate-500 uppercase tracking-widest">Qté</th>
                    <th className="py-3 px-4 text-right text-[9px] font-black text-slate-500 uppercase tracking-widest">P.U.</th>
                    <th className="py-3 px-4 text-right text-[9px] font-black text-slate-500 uppercase tracking-widest rounded-r-lg">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunity.items.map((item, idx) => (
                    <tr key={item.id || idx} className="border-b border-slate-100 last:border-0">
                      <td className="py-3.5 px-4 font-bold text-slate-800 text-sm">{item.label}</td>
                      <td className="py-3.5 px-4 text-right text-sm text-slate-600">{item.quantity}</td>
                      <td className="py-3.5 px-4 text-right text-sm text-slate-600">{Number(item.unitPrice).toFixed(2)} {symbol}</td>
                      <td className="py-3.5 px-4 text-right text-sm font-black text-slate-900">{Number(item.totalPrice ?? item.quantity * item.unitPrice).toFixed(2)} {symbol}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total */}
              <div className="flex justify-end mb-8">
                <div className="bg-indigo-600 text-white rounded-xl px-7 py-4 text-right min-w-[200px]">
                  <div className="text-[9px] font-bold uppercase tracking-widest opacity-80 mb-1">Total général</div>
                  <div className="text-2xl font-black">{Number(opportunity.totalAmount).toFixed(2)} {symbol}</div>
                </div>
              </div>

              {/* Comment */}
              {opportunity.comment && (
                <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl px-5 py-4 mb-10">
                  <div className="text-[9px] font-black text-amber-700 uppercase tracking-widest mb-2">Remarques</div>
                  <div className="text-xs text-amber-800 leading-relaxed whitespace-pre-wrap">{opportunity.comment}</div>
                </div>
              )}

              {/* Footer */}
              <div className="border-t border-slate-200 pt-5 flex justify-between items-center">
                <div className="text-[10px] text-slate-400">
                  Document généré le {new Date().toLocaleDateString('fr-FR')}{companyInfo.name ? ` · ${companyInfo.name}` : ''}
                </div>
                <div className="text-[10px] text-slate-300">{refNum}</div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default OpportunityPDFModal;
